import { useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';

import { Avatar } from '../components/Avatar.js';
import { AvatarLia } from '../lia/AvatarLia.js';
import { cn } from '../lib/cn.js';
import { initialesDe } from './ChampAvecMentions.js';
import { MENTION_INCONNUE, type EntreeDeMention } from './mentions.js';
import { AUTEUR_LIA, AUTEUR_SYSTEME, type MessageVue, type PortDeFil } from './port.js';
import { TexteAvecMentions } from './TexteAvecMentions.js';
import { TexteDuMessage } from './TexteDuMessage.js';
import { BarreDActionsDuMessage, PastillesDeReactions } from './BarreDActionsDuMessage.js';
import type { CleDeReaction } from './reactions.js';

export interface FilDeConversationProps<M extends MessageVue = MessageVue> {
  conversationId: string;
  port: Pick<PortDeFil<M>, 'observerConversation' | 'observerFil' | 'reagir' | 'retirerReaction'>;
  /** Le personId de la personne connectee (sa ligne porte « vous »). */
  moi: string | null;
  entrees: readonly EntreeDeMention[];
  /** Montrer un FIL (la racine et ses reponses) au lieu de la conversation. */
  parentId?: string | undefined;
  /** La fenetre de la conversation (defaut 50). */
  limite?: number | undefined;
  /** Le panneau de 24rem du centre. */
  compact?: boolean | undefined;
  /** On lit sans ecrire (G16) : ni reactions, et le fil se « voit » au lieu de se repondre. */
  lectureSeule?: boolean | undefined;
  /** Ce que la vue montre quand il n'y a encore aucun message. */
  vide?: string | undefined;
  /** Ouvrir le fil d'un message racine (Canaux, le centre). Absent : pas de geste. */
  onOuvrirFil?: ((message: M) => void) | undefined;
  /** Le nombre de reponses par racine, si l'hote le connait. */
  reponsesDe?: ((message: M) => number) | undefined;
  /** Les messages deja connus (rendu statique, test, page de preuve) : affiches avant la premiere ecoute. */
  messagesInitiaux?: M[] | undefined;
  /**
   * Le message a montrer (le centre le passe depuis `message=` d'une
   * notification `citation`) : le fil defile jusqu'a lui et le met en avant
   * brievement, d'un lisere lime a gauche (G14, la marque de la ligne neuve).
   * Une reponse de fil vue depuis la conversation : c'est sa racine qui est
   * montree. Absent ou introuvable : rien ne change.
   */
  messageVise?: string | undefined;
  className?: string | undefined;
}

/** La duree de la mise en avant du message vise (`messageVise`). */
export const DUREE_MISE_EN_AVANT_MS = 2400;

/** Le separateur de jour (« Aujourd'hui », « Hier », « Mardi 30 septembre »). */
export function libelleDeJour(date: Date, maintenant = new Date()): string {
  const jour = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const ce = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate());
  const ecart = Math.round((ce.getTime() - jour.getTime()) / 86_400_000);
  if (ecart === 0) return "Aujourd'hui";
  if (ecart === 1) return 'Hier';
  const texte = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', ...(date.getFullYear() !== maintenant.getFullYear() ? { year: 'numeric' } : {}) });
  return texte.charAt(0).toLocaleUpperCase('fr') + texte.slice(1);
}

export function heureDe(date: Date): string {
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** Le nom d'un auteur : par la source des mentions ; « Lia » ; jamais un identifiant. */
export function nomDAuteur(auteur: string | undefined, entrees: readonly EntreeDeMention[]): string {
  if (auteur === AUTEUR_LIA) return 'Lia';
  if (auteur === AUTEUR_SYSTEME) return 'Système';
  const entree = auteur === undefined ? undefined : entrees.find((e) => e.genre === 'personne' && e.id === auteur);
  return entree?.libelle ?? MENTION_INCONNUE;
}

/** La photo d'un auteur, quand l'annuaire la connait ; Lia et le systeme n'en ont pas. */
export function photoDAuteur(auteur: string | undefined, entrees: readonly EntreeDeMention[]): string | undefined {
  if (auteur === undefined || auteur === AUTEUR_LIA || auteur === AUTEUR_SYSTEME) return undefined;
  return entrees.find((e) => e.genre === 'personne' && e.id === auteur)?.photoURL ?? undefined;
}

/** L'ecart au-dela duquel deux messages du meme auteur ne forment plus une serie (P21). */
export const ECART_DE_SERIE_MS = 5 * 60_000;

/**
 * DEUX MESSAGES DE LA MEME SERIE (P21, retouches du 06/10/2026) : meme
 * auteur, meme jour, moins de cinq minutes d'ecart, ni l'un ni l'autre signe
 * par Lia (sa signature se lit a chaque reponse). Le second ne repete ni le
 * nom ni l'heure ; seul le dernier d'une serie porte la pointe et l'avatar.
 */
export function memeSerie(
  m: { auteur?: string | undefined; createdAt: Date; signeLia?: unknown },
  voisin: { auteur?: string | undefined; createdAt: Date; signeLia?: unknown } | undefined,
): boolean {
  return voisin !== undefined && m.auteur !== undefined && m.auteur !== AUTEUR_SYSTEME && voisin.auteur === m.auteur
    && !m.signeLia && !voisin.signeLia
    && Math.abs(m.createdAt.getTime() - voisin.createdAt.getTime()) < ECART_DE_SERIE_MS
    && m.createdAt.toDateString() === voisin.createdAt.toDateString();
}

/**
 * LE COTE D'UN MESSAGE (P13 du 06/10/2026) : la personne connectee ecrit a
 * DROITE, les autres a GAUCHE, Lia comprise (elle n'est jamais « moi », meme
 * quand c'est moi qui l'ai mentionnee). Depuis P21 (retouches validees du
 * fondateur), chaque cote a sa BULLE (`.taki-bulle`, `styles/tokens.css`).
 */
export function coteDuMessage(auteur: string | undefined, moi: string | null): 'gauche' | 'droite' {
  return moi !== null && auteur === moi && auteur !== AUTEUR_LIA && auteur !== AUTEUR_SYSTEME ? 'droite' : 'gauche';
}

/**
 * LE FIL PLAT (G9) : avatar, nom, heure, puis le texte, separes par des
 * filets ; pas de bulle. Un evenement systeme est une ligne discrete. Un
 * message de Lia est signe « Lia · mentionnée par X · ses droits · journal ».
 * Pilote par props : `port={sdk.messages}`.
 */
export function FilDeConversation<M extends MessageVue = MessageVue>({
  conversationId, port, moi, entrees, parentId, limite, compact, lectureSeule, vide, onOuvrirFil, reponsesDe, messagesInitiaux, messageVise, className,
}: FilDeConversationProps<M>): JSX.Element {
  const [messages, setMessages] = useState<M[] | null>(messagesInitiaux ?? null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [erreurReaction, setErreurReaction] = useState<string | null>(null);
  // Les reactions ne bloquent rien : l'ecran attend l'ecoute du fil, qui rend
  // le compteur a tout le monde (la fenetre et Canaux lisent le meme document).
  const peutReagir = lectureSeule !== true && moi !== null && port.reagir !== undefined && port.retirerReaction !== undefined;
  const reagir = (messageId: string, cle: CleDeReaction, dejaPosee: boolean): void => {
    setErreurReaction(null);
    const geste = dejaPosee ? port.retirerReaction?.(messageId, cle) : port.reagir?.(messageId, cle);
    void geste?.catch((cause: unknown) => setErreurReaction(`La réaction n’a pas été enregistrée. ${cause instanceof Error ? cause.message : ''}`.trim()));
  };
  const conteneur = useRef<HTMLDivElement>(null);
  const suitLeBas = useRef(true);
  const dernierAffiche = useRef<string | undefined>(undefined);
  // La mise en avant du message vise : allumee au rendu (une notification
  // ouverte la montre tout de suite), eteinte apres DUREE_MISE_EN_AVANT_MS.
  const [enAvant, setEnAvant] = useState(messageVise !== undefined);
  const viseMontre = useRef<string | null>(null);

  useEffect(() => {
    setMessages(messagesInitiaux ?? null);
    setErreur(null);
    const onError = (cause: unknown) => setErreur(cause instanceof Error ? cause.message : String(cause));
    const arret = parentId !== undefined
      ? port.observerFil(conversationId, parentId, setMessages, onError)
      : port.observerConversation(conversationId, limite !== undefined ? { limite } : {}, setMessages, onError);
    return arret;
  }, [conversationId, parentId, limite, port]);

  const dernierId = messages?.at(-1)?.id;
  useLayoutEffect(() => {
    // Une reaction peut agrandir le fil sans changer le dernier message.
    // Suivre cette hauteur si l'on etait en bas, sans interrompre la lecture plus haut.
    const zone = conteneur.current;
    if (zone && (suitLeBas.current || dernierAffiche.current !== dernierId)) {
      zone.scrollTop = zone.scrollHeight;
      suitLeBas.current = true;
    }
    dernierAffiche.current = dernierId;
  }, [dernierId, messages]);

  // Dans la vue conversation, les reponses d'un fil restent sous leur racine : on les compte, on ne les etale pas.
  const lignes = useMemo(() => {
    if (messages === null) return [];
    if (parentId !== undefined) return messages;
    return messages.filter((m) => m.parentId === undefined || m.parentId === null);
  }, [messages, parentId]);
  const reponsesComptees = useMemo(() => {
    const compte = new Map<string, number>();
    if (messages === null || parentId !== undefined) return compte;
    for (const m of messages) if (m.parentId) compte.set(m.parentId, (compte.get(m.parentId) ?? 0) + 1);
    return compte;
  }, [messages, parentId]);

  // La ligne qui porte le message vise : lui-meme, ou sa racine quand c'est une
  // reponse de fil et que la vue montre la conversation (racines seules).
  const ligneVisee = useMemo(() => {
    if (messageVise === undefined || messages === null) return null;
    const m = messages.find((x) => x.id === messageVise);
    if (m === undefined) return null;
    return parentId === undefined && m.parentId ? m.parentId : m.id;
  }, [messageVise, messages, parentId]);

  // Defiler jusqu'a la ligne visee, UNE fois par message vise (une ligne qui
  // arrive ensuite reprend le comportement normal : on descend en bas).
  useEffect(() => {
    if (ligneVisee === null || viseMontre.current === messageVise) return;
    viseMontre.current = messageVise ?? null;
    const el = conteneur.current?.querySelector(`[data-message="${ligneVisee.replace(/["\\]/g, '')}"]`);
    if (el instanceof HTMLElement) el.scrollIntoView({ block: 'center' });
    setEnAvant(true);
    const minuterie = setTimeout(() => setEnAvant(false), DUREE_MISE_EN_AVANT_MS);
    return () => clearTimeout(minuterie);
  }, [ligneVisee, messageVise]);

  const avatar = compact ? 24 : 32;
  const texte = compact ? 'text-[0.8125rem] leading-5' : 'text-sm leading-[1.4rem]';

  return <div ref={conteneur} data-fil-de-conversation onScroll={(event) => {
    const zone = event.currentTarget;
    suitLeBas.current = zone.scrollHeight - zone.clientHeight - zone.scrollTop <= 8;
  }} className={cn('taki-scroll-mask min-h-0 flex-1 overflow-y-auto overscroll-contain bg-surface pb-4', compact ? 'px-3' : 'px-5', className)}>
    {erreur !== null && <p role="alert" className="py-3 text-sm text-danger-ink">Le fil est illisible : {erreur}</p>}
    {erreurReaction !== null && <p role="alert" className="py-2 text-xs text-danger-ink">{erreurReaction}</p>}
    {messages !== null && lignes.length === 0 && erreur === null && <p className="py-6 text-center text-sm text-ink-500">{vide ?? 'Aucun message pour le moment. Le premier est à vous.'}</p>}
    {lignes.map((m, index) => {
      const precedent = lignes[index - 1];
      const jour = precedent === undefined || libelleDeJour(precedent.createdAt) !== libelleDeJour(m.createdAt) ? libelleDeJour(m.createdAt) : null;
      const suivant = lignes[index + 1];
      const estLia = m.auteur === AUTEUR_LIA;
      const estSysteme = m.auteur === AUTEUR_SYSTEME;
      const estMoi = coteDuMessage(m.auteur, moi) === 'droite';
      const nom = nomDAuteur(m.auteur, entrees);
      const reponses = reponsesDe?.(m) ?? reponsesComptees.get(m.id) ?? 0;
      const vise = enAvant && ligneVisee === m.id;
      const lisere = vise ? <span aria-hidden="true" data-lisere-vise className={cn('absolute bottom-0 top-0 w-[0.1875rem] bg-accent', compact ? '-left-3' : '-left-5')} /> : null;
      const suite = memeSerie(m, precedent);
      const dernierDeSerie = !memeSerie(m, suivant);
      return <div key={m.id}>
        {jour !== null && <div role="separator" data-jour-du-fil className="taki-jour-du-fil">{jour}</div>}
        {estSysteme
          ? <div data-message-systeme data-message={m.id} data-message-vise={vise || undefined} className="relative mx-auto my-2 flex max-w-[90%] items-baseline justify-center gap-2 text-center text-xs leading-4 text-ink-500">
            {lisere}
            <span className="min-w-0"><TexteAvecMentions texte={m.texte ?? ''} entrees={entrees} moi={moi} /></span>
            <time className="shrink-0 text-[0.6875rem] text-ink-500" dateTime={m.createdAt.toISOString()}>{heureDe(m.createdAt)}</time>
          </div>
          // P21 : une bulle par message, a gauche pour les autres (Lia comprise), a droite pour moi (P13) ; nom
          // et heure au-dessus du premier d'une serie, avatar (la vraie photo) a cote du dernier, sans filet.
          : <article data-message={m.id} data-auteur={estLia ? 'lia' : 'personne'} data-message-moi={estMoi || undefined} data-message-vise={vise || undefined}
            data-serie={suite ? 'suite' : undefined} data-barre-message-hote
            className={cn('relative flex items-end gap-2.5', suite ? 'mt-1' : 'mt-4', estMoi && 'justify-end')}>
            {lisere}
            {/* V3-6 : Lia a SON avatar (la mascotte sur son rond lime), jamais une initiale. */}
            {!estMoi && (estLia
              ? <AvatarLia size={avatar} className={cn('mb-0.5', !dernierDeSerie && 'invisible')} />
              : <Avatar size={avatar} src={photoDAuteur(m.auteur, entrees)} initials={initialesDe(nom)} aria-hidden={!dernierDeSerie || undefined}
                className={cn('mb-0.5 bg-surface-3 text-ink-700', !dernierDeSerie && 'invisible')} />)}
            <div className={cn('taki-message-contenu flex min-w-0 max-w-[min(86%,calc(100%-2.5rem))] flex-col', estMoi ? 'items-end' : 'items-start')}>
              <div className={cn(suite ? 'sr-only' : 'mb-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-0.5', estMoi && !suite && 'justify-end', compact ? 'text-xs' : 'text-[0.8125rem]')}>
                <span className="font-semibold text-ink-900">{estMoi ? 'Vous' : nom}</span>
                <time className="text-[0.6875rem] text-ink-500" dateTime={m.createdAt.toISOString()}>{heureDe(m.createdAt)}</time>
                {estLia && <span data-signature-lia className="rounded-pill border border-border px-1.5 text-[0.625rem] leading-4 text-ink-500">
                  Réponse AI · Appelé par {nomDAuteur(m.signeLia?.mentionnePar, entrees)}
                </span>}
              </div>
              <div className="taki-bulle-actions relative max-w-full">
                <div data-bulle className={cn('taki-bulle max-w-full', estMoi && 'taki-bulle-moi', dernierDeSerie && 'taki-bulle-pointe', texte)}>
                  <TexteDuMessage texte={m.texte ?? ''} auteur={m.auteur} entrees={entrees} moi={moi} />
                </div>
                <BarreDActionsDuMessage reactions={m.reactions} moi={moi} compacte={compact}
                  onReagir={peutReagir ? (cle, posee) => reagir(m.id, cle, posee) : undefined}
                  onRepondre={parentId === undefined && onOuvrirFil !== undefined ? () => onOuvrirFil(m) : undefined}
                  libelleRepondre={lectureSeule === true ? 'Voir le fil' : 'Répondre dans le fil'} />
              </div>
              <PastillesDeReactions reactions={m.reactions} moi={moi} onReagir={peutReagir ? (cle, posee) => reagir(m.id, cle, posee) : undefined} />
              {/* Le compteur des reponses reste visible ; « Repondre » n'est plus un texte sous chaque message (aucun espace vide) : c'est la fleche de la barre. */}
              {parentId === undefined && reponses > 0 && <div className="mt-1 flex items-center gap-2 text-xs leading-4 text-ink-500">
                {onOuvrirFil !== undefined
                  ? <button type="button" onClick={() => onOuvrirFil(m)} title="Voir le fil" className="font-semibold text-ink-600 underline-offset-2 hover:underline focus-visible:shadow-ring">
                    {reponses === 1 ? '1 réponse' : `${reponses} réponses`}
                  </button>
                  : <span>{reponses === 1 ? '1 réponse' : `${reponses} réponses`}</span>}
              </div>}
            </div>
          </article>}
      </div>;
    })}
  </div>;
}
